import { createContext, useContext, useEffect, useState } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updateProfile,
  sendEmailVerification,
  reload,
  setPersistence,
  browserLocalPersistence
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, googleProvider, db } from '../firebase';
import {
  setAnalyticsUserId,
  trackEmailVerificationRequired,
  trackEmailVerified,
  trackLogin,
  trackSignUp,
} from '../services/analyticsService';
import { getDefaultVoiceMinutesBase } from '../lib/voiceAccess';

const AuthContext = createContext();

export function requiresEmailVerification(user) {
  if (!user?.email || user.emailVerified) return false;
  return user.providerData.some((provider) => provider?.providerId === 'password');
}

export function useAuth() {
  return useContext(AuthContext);
}

async function ensureUserProfile(user) {
  if (!user?.uid) return;

  const userRef = doc(db, 'users', user.uid);
  const userSnap = await getDoc(userRef);
  const voiceDefaults = {
    voiceMinutesBase: getDefaultVoiceMinutesBase('free'),
    voiceMinutesBonus: 0,
    voiceMinutesUsed: 0,
  };

  if (!userSnap.exists()) {
    await setDoc(userRef, {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName || null,
      photoURL: user.photoURL || null,
      createdAt: serverTimestamp(),
      ...voiceDefaults,
    });
    console.log('Created minimal user profile on sign-in');
    return;
  }

  const data = userSnap.data() || {};
  const patch = {};
  if (user.email && !data.email) {
    patch.email = user.email;
  }
  if (user.displayName && !data.displayName) {
    patch.displayName = user.displayName;
  }
  if (user.photoURL && !data.photoURL) {
    patch.photoURL = user.photoURL;
  }
  if (!Number.isFinite(Number(data.voiceMinutesBase))) {
    patch.voiceMinutesBase = voiceDefaults.voiceMinutesBase;
  }
  if (!Number.isFinite(Number(data.voiceMinutesBonus))) {
    patch.voiceMinutesBonus = voiceDefaults.voiceMinutesBonus;
  }
  if (!Number.isFinite(Number(data.voiceMinutesUsed))) {
    patch.voiceMinutesUsed = voiceDefaults.voiceMinutesUsed;
  }

  if (Object.keys(patch).length > 0) {
    await setDoc(userRef, patch, { merge: true });
  }
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [emailVerificationRequired, setEmailVerificationRequired] = useState(false);
  const [loading, setLoading] = useState(true);

  function signup(email, password, displayName) {
    return createUserWithEmailAndPassword(auth, email, password)
      .then((result) => {
        const profileUpdate = displayName?.trim()
          ? updateProfile(result.user, { displayName: displayName.trim() })
          : Promise.resolve();

        return profileUpdate.then(() => {
          return sendEmailVerification(result.user).then(() => {
            trackSignUp('email');
            trackEmailVerificationRequired('email');
            setEmailVerificationRequired(true);
            return result;
          });
        });
      });
  }

  function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password)
      .then((result) => {
        trackLogin('email');
        return result;
      });
  }

  function loginWithGoogle() {
    return signInWithPopup(auth, googleProvider)
      .then((result) => {
        // Check if this is a new user
        const isNewUser = result.user.metadata.creationTime === result.user.metadata.lastSignInTime;
        if (isNewUser) {
          trackSignUp('google');
        } else {
          trackLogin('google');
        }
        return ensureUserProfile(result.user).then(() => result);
      });
  }

  function logout() {
    return signOut(auth);
  }

  function resendEmailVerification() {
    if (!auth.currentUser) {
      return Promise.reject(new Error('No authenticated user'));
    }

    return sendEmailVerification(auth.currentUser);
  }

  async function refreshAuthUser() {
    if (!auth.currentUser) {
      setCurrentUser(null);
      setEmailVerificationRequired(false);
      return false;
    }

    const wasVerificationRequired = emailVerificationRequired;
    await reload(auth.currentUser);
    const verificationRequired = requiresEmailVerification(auth.currentUser);
    setEmailVerificationRequired(verificationRequired);
    if (wasVerificationRequired && !verificationRequired) {
      trackEmailVerified('email');
    }
    if (!verificationRequired) {
      await ensureUserProfile(auth.currentUser);
    }
    return !verificationRequired;
  }

  useEffect(() => {
    // Force local persistence on iOS
    setPersistence(auth, browserLocalPersistence)
      .then(() => {
        console.log('Persistence set');
      })
      .catch((error) => {
        console.error('Persistence error:', error);
      });
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      setEmailVerificationRequired(requiresEmailVerification(user));

      // Set analytics user ID
      setAnalyticsUserId(user ? user.uid : null);

      // Phase 8: Auto-create minimal user profile on sign-in (like mobile does)
      // This ensures the user document exists before onboarding
      if (user && !requiresEmailVerification(user)) {
        try {
          await ensureUserProfile(user);
        } catch (error) {
          console.error('Error creating user profile on sign-in:', error);
        }
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    const syncAnalyticsUser = () => {
      setAnalyticsUserId(currentUser ? currentUser.uid : null);
    };

    window.addEventListener('aprendos:consentchange', syncAnalyticsUser);
    return () => window.removeEventListener('aprendos:consentchange', syncAnalyticsUser);
  }, [currentUser]);

  const value = {
    currentUser,
    user: currentUser,
    emailVerificationRequired,
    loading,
    signup,
    login,
    loginWithGoogle,
    logout,
    resendEmailVerification,
    refreshAuthUser
  };

  // Render children immediately - don't block on auth loading
  // Components that need auth can check the loading state themselves
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
